// "Tag with an area" beside the status control (owner, 2026-10-09: "the
// selection floating panel when it opens, it should have area dropdown like
// issue area drop down, along with status drop down, or the middle panel tab
// selection should also have the area added").
//
// THE ISSUE PAGE'S OWN PICKER (LocationPicker), in its pick-only area mode:
// drawn, approved zones only, each with its colour, nothing typed. Two
// pickers over the same zones would offer different lists the first time
// somebody added one.
//
// WHAT IS WRITTEN is the caller's write target - the same {uniqueIds} or
// {selectionId} the status button uses, so the count on this button and the
// set it tags cannot disagree with the status button beside it. A filter is
// never sent: it is not a write shape (selectionList.writeTarget).
import React, { useState } from "react";
import { Button, Space, Tooltip, message } from "antd";
import LocationPicker, { useLocationOptions } from "components/Coordination/LocationPicker";
import { tagElementArea } from "api/tracking/trackingElementsApi";

// A list longer than this is not sent as ids: a box that big is held by the
// server as a selection, and sending a cut-down list would tag a part of it
// while the button said all of it.
export const TAG_ID_CAP = 20000;

export const bareAreaId = (v) => {
  const m = /^area:([0-9a-f]{24})$/i.exec(String(v || ""));
  return m ? m[1] : (/^[0-9a-f]{24}$/i.test(String(v || "")) ? String(v) : "");
};

// What the tag call would send, or why it will not: { body, count } | { refuse }.
export function tagRequest(target, areaValue) {
  const areaId = bareAreaId(areaValue);
  if (!areaId) return { refuse: "Pick an area" };
  const b = (target && target.body) || null;
  const n = Number(target && target.count) || 0;
  if (!b || !n) return { refuse: "Select elements in the model first" };
  if (Array.isArray(b.uniqueIds) && b.uniqueIds.length) {
    if (b.uniqueIds.length > TAG_ID_CAP) {
      return { refuse: `${b.uniqueIds.length.toLocaleString()} elements is more than ${TAG_ID_CAP.toLocaleString()} - select them with a box so the server holds the selection` };
    }
    return { count: b.uniqueIds.length, body: { areaId, uniqueIds: b.uniqueIds } };
  }
  if (b.selectionId) return { count: n, body: { areaId, selectionId: b.selectionId } };
  return { refuse: "Select elements in the model first" };
}

export default function AreaTag({ projectId, target, canEdit = false, onTagged = null, size = "small", width = 200 }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const opts = useLocationOptions(projectId);
  const req = tagRequest(target, value);
  const area = ((opts && opts.areas) || []).find((a) => a.value === value);
  const name = area ? area.label : "";
  const go = async () => {
    if (req.refuse) return;
    setBusy(true);
    try {
      const r = await tagElementArea(projectId, req.body);
      message.success(`${Number((r && r.tagged) ?? req.count).toLocaleString()} element(s) tagged ${r && r.areaName ? r.areaName : name}`);
      if (onTagged) onTagged(r, req);
    } catch (e) {
      message.error(e.message || "Could not tag the area");
    } finally { setBusy(false); }
  };
  if (!canEdit) return null;
  return (
    <Space size={6} wrap>
      <LocationPicker projectId={projectId} value={{ area: value }} onChange={(v) => setValue((v && v.area) || "")}
        fields={["area"]} typed={false} size={size} width={width} />
      <Tooltip title={req.refuse || ""}>
        <Button size={size} loading={busy} disabled={!!req.refuse} onClick={go} data-testid="tag-area">
          {req.refuse ? "Tag area" : `Tag ${req.count.toLocaleString()} element${req.count === 1 ? "" : "s"} · ${name}`}
        </Button>
      </Tooltip>
    </Space>
  );
}
